import { LOWER_UUID_PATTERN } from "./kernel/kernel-internal.js";
import { z } from "zod";
//#region src/crate-names.ts
/**
* The crate name registry (plan 2026-10-17-crate-name-registry).
*
* Release-engine holds every cargo crate name that the cargo door may publish, with its owner: one
* app, or the platform (D17). This module is the one declaration of the name grammar, the body of
* the two replace routes, the view they return and the row of the directory route that the door
* reads. Release-engine, the `zion` CLI and `zion-ops` import these. The cargo door is plain `.mjs`
* with no dependencies, so it copies {@link CRATE_NAME_PATTERN} by value and tests the same cases.
*
* Every schema here validates and never rewrites. A zod `.transform()` on a schema that a
* controller binds throws in `createOpenApiDocument()` at boot, so there is none.
*
* The root barrel is browser-safe (`barrel-browser-safe.test.ts`). This module imports zod and
* `LOWER_UUID_PATTERN` from `./kernel/kernel-internal.js`. That file imports `@noble/hashes` and
* `./kernel-encoding.js`, so this module depends on them too.
*/
/**
* One crate name the registry stores: a lowercase letter, then at most 63 lowercase letters,
* digits, `_` or `-` (D5). The door's own grammar admits upper case and lowercases before the
* check, so every stored name is already the form the door compares.
*/
const CRATE_NAME_PATTERN = /^[a-z][a-z0-9_-]{0,63}$/;
/** The longest crate name, in characters: one letter plus 63 (D5). */
const CRATE_NAME_MAX_LENGTH = 64;
/**
* The most names that one app may hold in one set, the cap of the app replace body
* (`PUT /v1/apps/:appId/crate-names`, overview security table). The platform owner has its own
* cap, {@link PLATFORM_CRATE_NAMES_MAX} (R-001).
*/
const CRATE_NAMES_MAX = 100;
/** One crate name, exact and lowercase (D5). A prefix or a wildcard fails the pattern. */
const crateNameSchema = z.string().regex(CRATE_NAME_PATTERN, "a lowercase crate name");
/**
* The key two names share when cargo treats them as one crate: `_` maps to `-` (D5). The registry
* holds one live row per key, so `foo_bar` and `foo-bar` can never have two owners.
*/
function crateNameKey(name) {
	return name.replaceAll("_", "-");
}
/** True when two or more names in the list share one {@link crateNameKey}. */
function hasDuplicateKey(names) {
	return new Set(names.map(crateNameKey)).size !== names.length;
}
/** The schema of one replace body with at most `max` names, and no two names of one key. */
function replaceBodySchema(max) {
	return z.object({ crates: z.array(crateNameSchema).max(max) }).strict().refine((body) => !hasDuplicateKey(body.crates), {
		message: "two names in the list are one crate name",
		path: ["crates"]
	});
}
/**
* The body of `PUT /v1/apps/:appId/crate-names`: the whole set the app holds after the call (D7).
* An empty list removes every live name of the app.
*/
const crateNamesBodySchema = replaceBodySchema(100);
/**
* The most names the platform set may hold in one replace (ruling R-001). An app delete passes the
* app's names to the platform (D6), so the platform set can grow past {@link CRATE_NAMES_MAX}. A
* body of 1000 names of 64 characters is about 67 KB, under the 100 KiB JSON limit of
* release-engine's default route.
*/
const PLATFORM_CRATE_NAMES_MAX = 1e3;
/**
* The body of `PUT /v1/platform/crate-names`: the whole platform set after the call (D18, R-001).
* It has the grammar and the key rule of {@link crateNamesBodySchema}, with the cap
* {@link PLATFORM_CRATE_NAMES_MAX}.
*/
const platformCrateNamesBodySchema = replaceBodySchema(PLATFORM_CRATE_NAMES_MAX);
/**
* The view the four owner routes return: the live names of one owner, sorted. The view has no
* {@link CRATE_NAMES_MAX} cap: a deleted app's names pass to the platform (D6), so the platform set
* can hold more names than one replace body may send.
*/
const crateNamesViewSchema = z.object({ crates: z.array(crateNameSchema) }).strict();
/**
* One row of `GET /v1/crate-names`: a live name and its owner app. `appId` is `null` when the
* platform owns the name (D17). The door admits a per-app publish of the name only for a caller
* with a publish grant on `appId`, and a platform name only for a site-wide caller (D10).
*/
const crateNameDirectoryRowSchema = z.object({
	name: crateNameSchema,
	appId: z.string().regex(LOWER_UUID_PATTERN, "a lower-case UUID").nullable()
}).strict();
/**
* The most rows `GET /v1/crate-names` returns. The door refuses a longer answer and fails closed
* with 503, as it does for the apps directory (`APPS_DIRECTORY_MAX_ENTRIES`, 5000).
*/
const CRATE_NAME_DIRECTORY_MAX_ENTRIES = 5e3;
/** The body of `GET /v1/crate-names`: a bare array of rows, the shape of `GET /v1/apps`. */
const crateNameDirectorySchema = z.array(crateNameDirectoryRowSchema).max(CRATE_NAME_DIRECTORY_MAX_ENTRIES);
/** The directory route the cargo door reads with the caller's bearer (D12). */
const CRATE_NAME_DIRECTORY_PATH = "/v1/crate-names";
/** The platform-owned set (D18). */
const PLATFORM_CRATE_NAMES_PATH = "/v1/platform/crate-names";
/**
* The path of one app's set. The id must be a lower-case UUID: the route resolves no slug, and
* the CLI resolves a slug to the id first.
*
* @throws {RangeError} when `appId` is not a lower-case UUID.
*/
function appCrateNamesPath(appId) {
	if (!LOWER_UUID_PATTERN.test(appId)) throw new RangeError("appCrateNamesPath: the app id must be a lower-case UUID");
	return `/v1/apps/${appId}/crate-names`;
}
//#endregion
export { CRATE_NAMES_MAX, CRATE_NAME_DIRECTORY_MAX_ENTRIES, CRATE_NAME_DIRECTORY_PATH, CRATE_NAME_MAX_LENGTH, CRATE_NAME_PATTERN, PLATFORM_CRATE_NAMES_MAX, PLATFORM_CRATE_NAMES_PATH, appCrateNamesPath, crateNameDirectoryRowSchema, crateNameDirectorySchema, crateNameKey, crateNameSchema, crateNamesBodySchema, crateNamesViewSchema, platformCrateNamesBodySchema };

//# sourceMappingURL=crate-names.js.map